/**
 * AssessmentDesignBlueprints.jsx — Phase 2A-4
 * State machine: DRAFT → PUBLISHED → LOCKED
 * Primary guided journey: Blueprint list → Detail with stepper.
 * Components, publish blockers, optimistic concurrency.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, X, Lock, ChevronLeft, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDesignShell, useIsDesigner } from "../components/assessment-design/AssessmentDesignShell";
import { LifecycleBadge } from "../components/assessment-design/LifecycleBadge";
import { PublishBlockers } from "../components/assessment-design/PublishBlockers";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { "Authorization": `Bearer ${localStorage.getItem("academiaos_token")}`, "Content-Type": "application/json" }; }
function apiError(e, fb = "Error") {
  const d = e?.detail || e?.message || fb;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join("; ");
  if (typeof d === "object") return JSON.stringify(d);
  return String(d);
}

const ASSESSMENT_TYPES = ["ASSIGNMENT","ATTENDANCE","EXAM","OTHER","PARTICIPATION","PRACTICAL","PRESENTATION","PROJECT","QUIZ","VIVA"];
const ABSENCE_TREATMENTS = ["ZERO_CONTRIBUTION","EXCLUDE_FROM_DENOMINATOR","MEDICAL_DEFERRAL"];
const ASSESSMENT_SCOPES = ["SUMMATIVE","FORMATIVE","DIAGNOSTIC"];

const EMPTY_BP = { code: "", title: "", section_id: "", description: "", blueprint_type: "SECTION_BLUEPRINT", absence_treatment: "ZERO_CONTRIBUTION", assessment_scope: "SUMMATIVE" };
const EMPTY_COMP = { code: "", title: "", assessment_type: "EXAM", weight_pct: "", max_marks: "" };

function computeBlockers(bp, components) {
  const b = [];
  if (!bp) return b;
  if (bp.status === "PUBLISHED") { b.push({ key: "published", message: "Blueprint is already published and immutable." }); return b; }
  if (bp.status === "LOCKED")    { b.push({ key: "locked",    message: "Blueprint is locked." }); return b; }
  if (!components.length)        b.push({ key: "no-components",  message: "No components added. Add at least one component." });
  const totalWeight = components.reduce((s, c) => s + parseFloat(c.weight_pct || 0), 0);
  if (components.length > 0 && Math.abs(totalWeight - 100) > 0.01)
    b.push({ key: "weights", message: `Component weights total ${totalWeight.toFixed(2)}% — must equal 100%.` });
  return b;
}

export default function AssessmentDesignBlueprints() {
  const isDesigner = useIsDesigner();
  const [blueprints, setBlueprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);       // detail view blueprint id
  const [bpDetail, setBpDetail] = useState(null);
  const [components, setComponents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_BP);
  const [saving, setSaving] = useState(false);
  const [showAddComp, setShowAddComp] = useState(false);
  const [compForm, setCompForm] = useState(EMPTY_COMP);
  const [addingComp, setAddingComp] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [locking, setLocking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/assessment/blueprints`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setBlueprints(await r.json());
    } catch (e) { toast.error("Failed to load blueprints: " + apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (bp) => {
    setSelected(bp.id);
    setBpDetail(bp);
    setDetailLoading(true);
    try {
      const [bpR, compR] = await Promise.all([
        fetch(`${API}/assessment/blueprints/${bp.id}`, { headers: authH() }),
        fetch(`${API}/assessment/blueprints/${bp.id}/components`, { headers: authH() }),
      ]);
      const bpData = await bpR.json();
      const compData = await compR.json();
      if (!bpR.ok) throw bpData;
      if (!compR.ok) throw compData;
      setBpDetail(bpData);
      setComponents(compData);
    } catch (e) { toast.error("Failed to load blueprint detail: " + apiError(e)); }
    finally { setDetailLoading(false); }
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.description) delete body.description;
      const r = await fetch(`${API}/assessment/blueprints`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const text = await r.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text || `HTTP ${r.status}` }; }
      if (!r.ok) throw data;
      toast.success(`Blueprint "${data.title}" created`);
      setShowCreate(false);
      setForm(EMPTY_BP);
      load();
      openDetail(data);
    } catch (e) {
      const msg = apiError(e);
      if (msg.includes("section") || msg.includes("fk_aap_section")) {
        toast.error("Section not found — the Section ID you entered does not exist. Check the UUID and try again.");
      } else {
        toast.error("Create failed: " + msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddComponent = async (e) => {
    e.preventDefault();
    setAddingComp(true);
    try {
      const r = await fetch(`${API}/assessment/blueprints/${selected}/components`, {
        method: "POST", headers: authH(),
        body: JSON.stringify({
          code: compForm.code, title: compForm.title,
          assessment_type: compForm.assessment_type,
          weight_pct: parseFloat(compForm.weight_pct),
          max_marks: parseFloat(compForm.max_marks),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Component "${data.title}" added`);
      setShowAddComp(false);
      setCompForm(EMPTY_COMP);
      openDetail(bpDetail);
    } catch (e) { toast.error("Add component failed: " + apiError(e)); }
    finally { setAddingComp(false); }
  };

  const handleRemoveComponent = async (compId) => {
    try {
      const r = await fetch(`${API}/assessment/blueprints/${selected}/components/${compId}`, {
        method: "DELETE", headers: authH(),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw d; }
      toast.success("Component removed");
      openDetail(bpDetail);
    } catch (e) { toast.error("Remove failed: " + apiError(e)); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const idemKey = `pub-bp-${selected}-${Date.now()}`;
      const r = await fetch(`${API}/assessment/blueprints/${selected}/publish`, {
        method: "POST", headers: { ...authH(), "Idempotency-Key": idemKey },
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Blueprint "${data.title}" published`);
      openDetail(data);
      load();
    } catch (e) {
      const d = e?.detail;
      if (d?.error === "stale_version") toast.error("Stale version — refresh and try again.");
      else if (typeof d === "string" && d.includes("weight")) toast.error("Weight integrity error: " + d);
      else toast.error("Publish failed: " + apiError(e));
    } finally { setPublishing(false); }
  };

  const handleLock = async () => {
    setLocking(true);
    try {
      const r = await fetch(`${API}/assessment/blueprints/${selected}/lock`, {
        method: "POST", headers: authH(),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Blueprint locked");
      openDetail(data);
      load();
    } catch (e) { toast.error("Lock failed: " + apiError(e)); }
    finally { setLocking(false); }
  };

  const totalWeight = components.reduce((s, c) => s + parseFloat(c.weight_pct || 0), 0);
  const blockers = bpDetail ? computeBlockers(bpDetail, components) : [];
  const canPublish = isDesigner && bpDetail?.status === "DRAFT" && !blockers.filter(b => b.key !== "published" && b.key !== "locked").length;

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selected && bpDetail) {
    return (
      <AssessmentDesignShell title={bpDetail.title} breadcrumb="Blueprints">
        <div className="mb-6">
          <button onClick={() => { setSelected(null); setBpDetail(null); setComponents([]); }}
            data-testid="blueprint-back-btn"
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Blueprints
          </button>
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-100">{bpDetail.title}</h2>
                <LifecycleBadge status={bpDetail.status} />
              </div>
              <p className="text-xs text-slate-500 mt-1 font-mono">{bpDetail.code}</p>
            </div>
            {isDesigner && bpDetail.status === "DRAFT" && (
              <div className="flex items-center gap-2">
                <button onClick={() => setShowAddComp(true)} data-testid="add-component-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Add Component
                </button>
                <button onClick={handlePublish} disabled={publishing || !canPublish}
                  data-testid="blueprint-publish-btn"
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50 transition-colors">
                  {publishing ? "Publishing…" : "Publish Blueprint"}
                </button>
              </div>
            )}
            {isDesigner && bpDetail.status === "PUBLISHED" && (
              <button onClick={handleLock} disabled={locking} data-testid="blueprint-lock-btn"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm bg-violet-900/40 hover:bg-violet-800/60 text-violet-300 border border-violet-700/50 disabled:opacity-50 transition-colors">
                <Lock className="w-3.5 h-3.5" />
                {locking ? "Locking…" : "Lock Blueprint"}
              </button>
            )}
          </div>
        </div>

        {/* Publish blockers */}
        {bpDetail.status === "DRAFT" && <PublishBlockers blockers={blockers} className="mb-6" />}

        {/* Component weight progress — always visible in detail view */}
        <div data-testid="weight-progress" className="mb-6 rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-400">Component Weight Allocation</span>
            <span className={`text-xs font-mono font-bold ${components.length === 0 ? "text-slate-500" : Math.abs(totalWeight - 100) < 0.01 ? "text-emerald-400" : "text-amber-400"}`}>
              {totalWeight.toFixed(2)}% / 100%
            </span>
          </div>
          <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${components.length === 0 ? "bg-slate-700" : Math.abs(totalWeight - 100) < 0.01 ? "bg-emerald-500" : totalWeight > 100 ? "bg-red-500" : "bg-amber-500"}`}
              style={{ width: `${Math.min(totalWeight, 100)}%` }}
            />
          </div>
          {components.length === 0 && (
            <p className="text-xs text-slate-500 mt-1.5">Add components to allocate weights.</p>
          )}
          {components.length > 0 && Math.abs(totalWeight - 100) > 0.01 && (
            <p className="text-xs text-amber-400 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {totalWeight < 100 ? `${(100 - totalWeight).toFixed(2)}% unallocated` : `${(totalWeight - 100).toFixed(2)}% over-allocated`}
            </p>
          )}
        </div>

        {/* Components list */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-300">Components ({components.length})</h3>
          </div>
          {detailLoading ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading components…</div>
          ) : components.length === 0 ? (
            <div data-testid="components-empty" className="p-8 text-center text-slate-500 text-sm">
              No components yet.{" "}
              {isDesigner && bpDetail.status === "DRAFT" && (
                <button onClick={() => setShowAddComp(true)} className="text-sky-400 hover:text-sky-300">Add a component →</button>
              )}
            </div>
          ) : (
            <div data-testid="components-list">
              {components.map((comp, idx) => (
                <div key={comp.id} data-testid={`component-row-${comp.id}`}
                  className="flex items-center gap-4 px-4 py-3 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/30 transition-colors">
                  <span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs text-slate-400 flex-shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-slate-200">{comp.title}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-500 font-mono">{comp.code}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded border border-slate-700 text-slate-400">{comp.assessment_type}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-semibold text-slate-200">{parseFloat(comp.weight_pct).toFixed(1)}%</span>
                    <p className="text-xs text-slate-500">{parseFloat(comp.max_marks).toFixed(0)} marks</p>
                  </div>
                  {isDesigner && bpDetail.status === "DRAFT" && (
                    <button onClick={() => handleRemoveComponent(comp.id)}
                      data-testid={`remove-component-btn-${comp.id}`}
                      className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-950/30 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Blueprint metadata */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            ["Type", bpDetail.blueprint_type],
            ["Scope", bpDetail.assessment_scope],
            ["Absence", bpDetail.absence_treatment],
            ["Version", bpDetail.version_no],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-800 bg-slate-900/30 px-3 py-2">
              <div className="text-xs text-slate-500 mb-0.5">{k}</div>
              <div className="text-sm text-slate-300 font-mono">{v}</div>
            </div>
          ))}
        </div>

        {/* Add component modal */}
        {showAddComp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div data-testid="add-component-modal" className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
                <h2 className="text-base font-semibold text-slate-100">Add Component</h2>
                <button onClick={() => { setShowAddComp(false); setCompForm(EMPTY_COMP); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <form onSubmit={handleAddComponent} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Code *</label>
                    <input data-testid="comp-code-input" value={compForm.code}
                      onChange={e => setCompForm(f => ({ ...f, code: e.target.value }))} required
                      placeholder="e.g. EXAM-01"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Type *</label>
                    <select data-testid="comp-type-select" value={compForm.assessment_type}
                      onChange={e => setCompForm(f => ({ ...f, assessment_type: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                      {ASSESSMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                  <input data-testid="comp-title-input" value={compForm.title}
                    onChange={e => setCompForm(f => ({ ...f, title: e.target.value }))} required
                    placeholder="Component title"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Weight % *</label>
                    <input data-testid="comp-weight-input" type="number" step="0.01" min="0" max="100"
                      value={compForm.weight_pct} onChange={e => setCompForm(f => ({ ...f, weight_pct: e.target.value }))} required
                      placeholder="e.g. 60"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Max Marks *</label>
                    <input data-testid="comp-marks-input" type="number" step="0.01" min="0"
                      value={compForm.max_marks} onChange={e => setCompForm(f => ({ ...f, max_marks: e.target.value }))} required
                      placeholder="e.g. 100"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg px-3 py-2 text-xs text-slate-400">
                  Current total: <span className="font-mono text-slate-300">{totalWeight.toFixed(2)}%</span>
                  {compForm.weight_pct && (
                    <> + {parseFloat(compForm.weight_pct || 0).toFixed(2)}% = <span className={`font-mono font-semibold ${Math.abs(totalWeight + parseFloat(compForm.weight_pct || 0) - 100) < 0.01 ? "text-emerald-400" : "text-amber-400"}`}>
                      {(totalWeight + parseFloat(compForm.weight_pct || 0)).toFixed(2)}%</span></>
                  )}
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setShowAddComp(false); setCompForm(EMPTY_COMP); }}
                    className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">Cancel</button>
                  <button type="submit" disabled={addingComp} data-testid="add-component-submit-btn"
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                    {addingComp ? "Adding…" : "Add Component"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AssessmentDesignShell>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <AssessmentDesignShell title="Assessment Blueprints" breadcrumb="Blueprints">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">
          Blueprints define the assessment structure for a section.{" "}
          <span className="font-mono text-slate-300">DRAFT → PUBLISHED → LOCKED</span>
        </p>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="blueprints-refresh-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isDesigner && (
            <button onClick={() => setShowCreate(true)} data-testid="blueprint-create-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors">
              <Plus className="w-4 h-4" /> New Blueprint
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 rounded-lg bg-slate-800/40 animate-pulse" />)}</div>
      ) : blueprints.length === 0 ? (
        <div data-testid="blueprints-empty" className="text-center py-16 text-slate-500 text-sm">
          No blueprints yet.
          {isDesigner && <button onClick={() => setShowCreate(true)} className="block mt-2 text-sky-400 hover:text-sky-300 mx-auto">Create first blueprint →</button>}
        </div>
      ) : (
        <div data-testid="blueprints-list" className="space-y-2">
          {blueprints.map(bp => (
            <button key={bp.id} data-testid={`blueprint-row-${bp.id}`}
              onClick={() => openDetail(bp)}
              className="w-full text-left flex items-center gap-4 p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:border-sky-800 hover:bg-slate-900/80 transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-slate-100 truncate group-hover:text-sky-300 transition-colors">{bp.title}</span>
                  <LifecycleBadge status={bp.status} />
                  {bp.status === "LOCKED" && <Lock className="w-3.5 h-3.5 text-violet-400" />}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-mono">{bp.code}</span>
                  <span>v{bp.version_no}</span>
                  <span>{bp.assessment_scope}</span>
                </div>
              </div>
              <ChevronLeft className="w-4 h-4 text-slate-600 group-hover:text-sky-500 rotate-180 transition-colors flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Create blueprint modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div data-testid="blueprint-create-modal" className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-base font-semibold text-slate-100">Create Blueprint</h2>
              <button onClick={() => { setShowCreate(false); setForm(EMPTY_BP); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Code *</label>
                  <input data-testid="blueprint-code-input" value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value }))} required
                    placeholder="e.g. BP-2026-01"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Section ID *</label>
                  <input data-testid="blueprint-section-input" value={form.section_id}
                    onChange={e => setForm(f => ({ ...f, section_id: e.target.value }))} required
                    placeholder="UUID of the section"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500 font-mono text-xs" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                <input data-testid="blueprint-title-input" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
                  placeholder="Blueprint title"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Scope</label>
                  <select data-testid="blueprint-scope-select" value={form.assessment_scope}
                    onChange={e => setForm(f => ({ ...f, assessment_scope: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                    {ASSESSMENT_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Absence Treatment</label>
                  <select data-testid="blueprint-absence-select" value={form.absence_treatment}
                    onChange={e => setForm(f => ({ ...f, absence_treatment: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                    {ABSENCE_TREATMENTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
                <textarea data-testid="blueprint-description-input" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Optional description"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500 resize-none" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_BP); }}
                  className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="blueprint-create-submit-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                  {saving ? "Creating…" : "Create Blueprint"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AssessmentDesignShell>
  );
}
